import { useState } from 'react'
import { Button } from '../../shared/ui'
import { supabase } from '../../lib/supabase'
import { getStages } from '../../lib/stages'
import { listPrograms } from '../../lib/outreach/api'
import type { Program } from '../../lib/outreach/types'
import { EnrollDialog } from '../shared/EnrollDialog'
import { CONTACTS_CHANGED, STATS_INVALIDATE } from '../shared/events'

/**
 * Floating bulk action bar shown when 1+ rows are selected. Three
 * actions: stage update, tag add, delete. Each one writes via the
 * supabase client and dispatches the legacy → island bus events so
 * the legacy stats bar refreshes.
 */

interface Props {
  selectedIds: string[]
  onClear: () => void
  onChanged: () => void
}

export function ContactsBulkBar({ selectedIds, onClear, onChanged }: Props) {
  const [stage, setStage] = useState('')
  const [tagInput, setTagInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [programs, setPrograms] = useState<Program[] | null>(null)

  if (!selectedIds.length) return null

  const dispatchAfter = () => {
    const bus = window.ShaunIslands?.events
    if (bus) {
      bus.dispatchEvent(new CustomEvent(CONTACTS_CHANGED))
      bus.dispatchEvent(new CustomEvent(STATS_INVALIDATE))
    }
    // Also refresh legacy stats (still owns funnel) — best effort.
    const refreshStats = (window as unknown as { loadStats?: () => void }).loadStats
    refreshStats?.()
    onClear()
    onChanged()
  }

  const announce = (msg: string, type?: 'success' | 'error') => {
    const t = (window as unknown as { toast?: (m: string, t?: string) => void }).toast
    t?.(msg, type)
  }

  const updateStage = async () => {
    if (!stage) return
    const userId = window.user?.id
    if (!userId) return
    setBusy(true)
    try {
      const rows = selectedIds.map((id) => ({
        user_id: userId,
        contact_id: id,
        lifecycle_stage: stage,
        updated_at: new Date().toISOString(),
      }))
      const { error } = await supabase
        .from('user_contacts')
        .upsert(rows, { onConflict: 'user_id,contact_id' })
      if (error) throw error
      announce(
        `Updated ${selectedIds.length} contact${selectedIds.length === 1 ? '' : 's'}`,
        'success',
      )
      setStage('')
      dispatchAfter()
    } catch (e) {
      announce('Stage update failed: ' + (e instanceof Error ? e.message : String(e)), 'error')
    } finally {
      setBusy(false)
    }
  }

  const addTag = async () => {
    const tag = tagInput
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\-_ ]/g, '')
    if (!tag) return
    const userId = window.user?.id
    if (!userId) return
    setBusy(true)
    let tagged = 0
    const failed: string[] = []
    try {
      for (const id of selectedIds) {
        try {
          const { data: uc, error: readErr } = await supabase
            .from('user_contacts')
            .select('tags')
            .eq('user_id', userId)
            .eq('contact_id', id)
            .maybeSingle()
          if (readErr) {
            failed.push(id)
            continue
          }
          const existing = ((uc as { tags?: string[] | null } | null)?.tags ?? []) as string[]
          if (!existing.includes(tag)) {
            const next = [...existing, tag]
            const { error: writeErr } = await supabase.from('user_contacts').upsert(
              {
                user_id: userId,
                contact_id: id,
                tags: next,
                updated_at: new Date().toISOString(),
              },
              { onConflict: 'user_id,contact_id' },
            )
            if (writeErr) {
              failed.push(id)
              continue
            }
          }
          tagged++
        } catch {
          failed.push(id)
        }
      }
      if (failed.length) {
        announce(`Tagged ${tagged} of ${selectedIds.length} — ${failed.length} failed`, 'error')
      } else {
        announce(`Tagged ${tagged} contact${tagged === 1 ? '' : 's'}`, 'success')
      }
      setTagInput('')
      dispatchAfter()
    } finally {
      setBusy(false)
    }
  }

  const del = async () => {
    if (
      !confirm(
        `Delete ${selectedIds.length} contact${selectedIds.length === 1 ? '' : 's'}? This cannot be undone.`,
      )
    )
      return
    setBusy(true)
    try {
      const { error } = await supabase.from('contacts').delete().in('id', selectedIds)
      if (error) throw error
      announce(
        `Deleted ${selectedIds.length} contact${selectedIds.length === 1 ? '' : 's'}`,
        'success',
      )
      dispatchAfter()
    } catch (e) {
      announce('Delete failed: ' + (e instanceof Error ? e.message : String(e)), 'error')
    } finally {
      setBusy(false)
    }
  }

  const openEnroll = async () => {
    setBusy(true)
    try {
      const list = await listPrograms()
      if (list.length === 0) {
        announce('Create a program first (Settings, Outreach programs)', 'error')
        return
      }
      setPrograms(list)
    } catch (e) {
      announce('Could not load programs: ' + (e instanceof Error ? e.message : String(e)), 'error')
    } finally {
      setBusy(false)
    }
  }

  const stages = getStages()

  return (
    <div
      id="bulk-bar"
      style={{
        position: 'sticky',
        bottom: 0,
        background: 'var(--bg2)',
        borderTop: '1px solid var(--border2)',
        padding: '10px 14px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center',
        marginTop: 8,
        zIndex: 10,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <span
        id="bulk-count"
        style={{ fontSize: 12, color: 'var(--text-dim)', fontFamily: 'var(--font-ui)' }}
      >
        {selectedIds.length} selected
      </span>
      <select
        className="filter-select"
        id="bulk-stage-sel"
        value={stage}
        disabled={busy}
        onChange={(e) => setStage(e.target.value)}
      >
        <option value="">Set stage…</option>
        {stages.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <Button variant="secondary" disabled={busy || !stage} onClick={updateStage}>
        Apply
      </Button>
      <input
        className="filter-select"
        id="bulk-tag-inp"
        placeholder="Add tag…"
        disabled={busy}
        value={tagInput}
        onChange={(e) => setTagInput(e.target.value)}
        style={{ width: 140 }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void addTag()
          }
        }}
      />
      <Button variant="secondary" disabled={busy || !tagInput.trim()} onClick={() => void addTag()}>
        Add tag
      </Button>
      <Button variant="secondary" disabled={busy} onClick={() => void openEnroll()}>
        Add to program
      </Button>
      <Button variant="danger" disabled={busy} onClick={() => void del()}>
        Delete
      </Button>
      <Button variant="secondary" disabled={busy} onClick={onClear} style={{ marginLeft: 'auto' }}>
        Clear
      </Button>
      {programs && window.user?.id && (
        <EnrollDialog
          programs={programs}
          source={{ kind: 'ids', ids: selectedIds }}
          userId={window.user.id}
          onClose={() => setPrograms(null)}
        />
      )}
    </div>
  )
}
